import _ from "underscore";
import DriveView from "../../DriveView";
import { Dialog } from "../framework/dialog";
import { handleApi } from "../../utils/error";
import Api from "../../services/api";
import { commonPasswords } from "../../utils/commonPasswords";
import toastr from "../../utils/toastr";
import Spinner from "../../utils/spinner";
import { NetworkStates, PageLevel } from "../../store/mobile/types";

import pageHTML from "./passwordChange.tmpl";
import { FeatureFlagName } from "../../utils/featureFlagName";
import { isMyAdminUser } from "../../utils/auth/isMyAdminUser";
import { signInWithNewPassword } from "../../modules/core/auth/services/signInWithNewPassword";
import type { ICredentialsWithServer } from "../../utils/credentialsStore";

export default class ChangePasswordView extends DriveView<Backbone.Model> {

    protected strings = [
        "Your password has been successfully changed."
    ];
    private readonly template = _.template(pageHTML);
    private readonly api: Api;
    private readonly waiting = new Spinner();

    constructor (options) {
        super(options, {
            title: "Password Change",
            activeSection: "settings",
            pageLevel: PageLevel.TWO
        });
        this.api = this.app.api;
    }

    changePassword (currentPassword, newPassword, newPasswordVerify) {
        this.startProgress();

        if (this.app.store.getState().mobile.online !== NetworkStates.ONLINE) {
            this.createPasswordErrorDialog("You cannot change your password while offline.");
            this.stopProgress();
            return;
        }

        void this.api.getSession(null).then(session => {
            let { credentials } = session;
            this.api.call(
                "Get",
                {
                    typeName: "User",
                    search: {
                        name: credentials.userName
                    }
                },
                data => {
                    let user = data[0];

                    // we need to check if they have the iamJwt token as there could be user who have been migrated
                    // but they can't login as an IAM user due to their software version
                    let isIamUser = !!user.iAMMetadata?.isIAMVerified && credentials.iamJwt;

                    if (!isIamUser && isMyAdminUser(user)) {
                        this.createPasswordErrorDialog("You are using a MyAdmin account. Passwords for accounts of this type must be changed through the MyAdmin portal.");
                        return;
                    }

                    let validationError = this.validatePasswords(currentPassword, newPassword, newPasswordVerify);
                    if (validationError) {
                        this.stopProgress();
                        this.createPasswordErrorDialog(validationError);
                        return;
                    }

                    if (isIamUser && !this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.DISABLE_IAM_LOGIN)) {
                        this.changePasswordForIamUser(user, currentPassword, newPassword);
                        return;
                    }
                    user.password = newPassword;
                    user.changePassword = false;

                    this.api.call(
                        "Authenticate",
                        {
                            database: credentials.database,
                            userName: credentials.userName,
                            password: currentPassword
                        },
                        () => {
                            // Successfully authenticated, old password is valid
                            // Set new password
                            this.api.call(
                                "Set",
                                {
                                    typeName: "User",
                                    entity: user
                                },
                                () => {
                                    this.updateCredentials(session, newPassword);
                                },
                                error => {
                                    this.stopProgress();
                                    if (error && error.data.type === "PasswordPolicyViolationException") {
                                        this.createPasswordErrorDialog(error.message || "This password is not allowed.");
                                    } else {
                                        handleApi(error);
                                    }
                                }
                            );
                        },
                        this.handleAuthError("InvalidUserException", "You did not enter the correct current password. Please try again.")
                    );
                },
                (err) => {
                    this.stopProgress();
                    handleApi(err);
                }
            );
        });
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #submitPassword": () => this.changePassword(this.el.querySelector("#currentPassword").value, this.el.querySelector("#newPassword").value, this.el.querySelector("#newPasswordVerify").value)
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render () {
        void this.display(this.template());
        return this;
    }

    onDone () {
        this.stopProgress();
        toastr.success(this.getString("Your password has been successfully changed."));
        this.app.router.navigate("settings/main", true);
    }

    createPasswordErrorDialog (errorMessage) {
        let errorDialog = new Dialog(
            "password-change-error",
            errorMessage,
            this.translator,
            "Password Change Error",
            [{
                id: "password-change-error-ok",
                text: "OK",
                callback: () => {
                    errorDialog.remove();
                }
            }]
        );

        void errorDialog.open();
    }

    private handleAuthError (errorType: string, errorMessage: string) {
        return (error) => {
            this.stopProgress();
            if (error && error.data.type === errorType) {
                this.createPasswordErrorDialog(errorMessage);
            } else {
                handleApi(error);
            }
        };
    }

    private updateCredentials (session: ICredentialsWithServer, newPassword: string) {
        void signInWithNewPassword(this.api, session, newPassword).then(
            () => this.onDone(),
            this.handleAuthError("InvalidUserException", "This password is not allowed.")
        );
    }

    private validatePasswords (oldPass: string, newPass: string, verifyPass: string): string {
        if (oldPass === "") {
            return "Please enter your current password.";
        }

        if (newPass === "") {
            return "Please enter your new password.";
        }

        if (newPass !== verifyPass) {
            return "Please enter matching passwords.";
        }

        if (commonPasswords.indexOf(newPass.toLowerCase()) > -1) {
            return "Common passwords are not allowed.";
        }

        return "";
    }

    private startProgress () {
        let currentPasswordInput = <HTMLInputElement> this.el.querySelector("#currentPassword");
        currentPasswordInput && currentPasswordInput.setAttribute("disabled", "disabled");

        let newPasswordInput = <HTMLInputElement> this.el.querySelector("#newPassword");
        newPasswordInput && newPasswordInput.setAttribute("disabled", "disabled");

        let newPasswordVerifyInput = <HTMLInputElement> this.el.querySelector("#newPasswordVerify");
        newPasswordVerifyInput && newPasswordVerifyInput.setAttribute("disabled", "disabled");

        let submitPasswordButton = <HTMLButtonElement> this.el.querySelector("#submitPassword");
        submitPasswordButton && submitPasswordButton.setAttribute("disabled", "disabled");
        submitPasswordButton && this.waiting.spin(submitPasswordButton);
    }

    private stopProgress () {
        let currentPasswordInput = <HTMLInputElement> this.el.querySelector("#currentPassword");
        currentPasswordInput && currentPasswordInput.removeAttribute("disabled");

        let newPasswordInput = <HTMLInputElement> this.el.querySelector("#newPassword");
        newPasswordInput && newPasswordInput.removeAttribute("disabled");

        let newPasswordVerifyInput = <HTMLInputElement> this.el.querySelector("#newPasswordVerify");
        newPasswordVerifyInput && newPasswordVerifyInput.removeAttribute("disabled");

        let submitPasswordButton = <HTMLButtonElement> this.el.querySelector("#submitPassword");
        submitPasswordButton && submitPasswordButton.removeAttribute("disabled");
        submitPasswordButton && this.waiting.stop();
    }

    private changePasswordForIamUser(user: IUser, oldPassword: string, newPassword: string) {
        this.api.callPromise("ChangeIamPassword", { userName: user.name, oldPassword, newPassword })
        .then(() => {
            if (!user.changePassword) {
                return Promise.resolve();
            }
            user.changePassword = false;
            return this.api.callPromise("Set", { typeName: "User", entity: user });
        }).then(() => {
            this.onDone();
        }).catch((err) => {
            this.stopProgress();
            if (err && err.data.type === "InvalidPermissionsException") {
                this.createPasswordErrorDialog("You did not enter the correct current password. Please try again.");
            } else if (err && err.data.type === "PasswordPolicyViolationException") {
                this.createPasswordErrorDialog("This password is not allowed.");
            } else {
                this.createPasswordErrorDialog("Password Change Error");
            }
        });
    }
}